import { useState } from 'react';
import { getFileExtension, isSharedFile, SharedFileOrDirectory } from '../SharedFiles.types';

export enum SharedFilesSortType {
  EditDate = 'editdate',
  EditedBy = 'editedby',
  FileType = 'filetype',
  Name = 'name',
  Size = 'size',
}

type SortOrder = 'ascending' | 'descending';

type SharedFilesSortState = SharedFilesSortType | null;

/**
 * Hook that stores the current sorting state and enables switching between sorting types.
 */
export function useSharedFilesSort() {
  const [sort, setSort] = useState<{ type: SharedFilesSortState; order: SortOrder }>();

  const toggle = (sortType: SharedFilesSortState) =>
    setSort((prevState) => {
      return {
        type: sortType,
        order: prevState?.type === sortType && prevState?.order === 'ascending' ? 'descending' : 'ascending',
      };
    });

  const sortByName = () => toggle(SharedFilesSortType.Name);
  const sortBySize = () => toggle(SharedFilesSortType.Size);
  const sortByFileType = () => toggle(SharedFilesSortType.FileType);
  const sortByEditedBy = () => toggle(SharedFilesSortType.EditedBy);
  const sortByEditDate = () => toggle(SharedFilesSortType.EditDate);

  const compare = (a: string | number | Date, b: string | number | Date): number => {
    if (a < b) {
      return sort?.order === 'ascending' ? -1 : 1;
    }

    if (a > b) {
      return sort?.order === 'ascending' ? 1 : -1;
    }

    return 0;
  };

  const handleNullOrUndefined = (
    a: string | number | Date | boolean | undefined | null,
    b: string | number | Date | boolean | undefined | null,
  ): number => {
    if (!a && b) {
      return sort?.order === 'ascending' ? -1 : 1;
    }

    if (a && !b) {
      return sort?.order === 'ascending' ? 1 : -1;
    }

    return 0;
  };

  const bySize = (a: SharedFileOrDirectory, b: SharedFileOrDirectory): number => {
    if (isSharedFile(a) && isSharedFile(b)) {
      return compare(a.fileSizeBytes, b.fileSizeBytes);
    }

    return handleNullOrUndefined(isSharedFile(a), isSharedFile(b));
  };

  const byFileType = (a: SharedFileOrDirectory, b: SharedFileOrDirectory): number => {
    return compare(getFileExtension(a), getFileExtension(b));
  };

  const byEditDate = (a: SharedFileOrDirectory, b: SharedFileOrDirectory): number => {
    if (a.editDate && b.editDate) {
      return compare(new Date(a.editDate), new Date(b.editDate));
    }

    return handleNullOrUndefined(a.editDate, b.editDate);
  };

  const byEditedBy = (a: SharedFileOrDirectory, b: SharedFileOrDirectory): number => {
    if (a.editedBy?.name && b.editedBy?.name) {
      return compare(a.editedBy.name, b.editedBy.name);
    }

    return handleNullOrUndefined(a.editedBy?.name, b.editedBy?.name);
  };

  const getName = (f: SharedFileOrDirectory): string => {
    // user can provide custom fileTitle, which will be displayed before fileName if provided.
    const name = isSharedFile(f) ? (f.fileTitle ?? f.fileName) : f.pageLink.name;
    return name.toLowerCase();
  };
  const byName = (a: SharedFileOrDirectory, b: SharedFileOrDirectory): number => {
    return compare(getName(a), getName(b));
  };

  const sortBy = (a: SharedFileOrDirectory, b: SharedFileOrDirectory) => {
    switch (sort?.type) {
      case SharedFilesSortType.Name:
        return byName(a, b);
      case SharedFilesSortType.EditDate:
        return byEditDate(a, b);
      case SharedFilesSortType.EditedBy:
        return byEditedBy(a, b);
      case SharedFilesSortType.FileType:
        return byFileType(a, b);
      case SharedFilesSortType.Size:
        return bySize(a, b);
      default:
        return 0;
    }
  };

  const getSortOrderByType = (type: SharedFilesSortType): SortOrder | undefined => {
    return sort && type === sort.type ? sort.order : undefined;
  };

  return {
    getSortOrderByType,
    sortBy,
    sortByEditDate,
    sortByEditedBy,
    sortByFileType,
    sortByName,
    sortBySize,
  };
}
